import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

// GBP is the reference display currency; checkout always charges NGN via Paystack (disclosed at payment).
export const CURRENCY_LIST = [
  { code: "USD", symbol: "$", label: "US dollar" },
  { code: "EUR", symbol: "€", label: "Euro" },
  { code: "GBP", symbol: "£", label: "British pound" },
  { code: "NGN", symbol: "₦", label: "Nigerian naira" },
];

const EU_REGIONS = ["AT", "BE", "CY", "DE", "EE", "ES", "FI", "FR", "GR", "HR", "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PT", "SI", "SK"];

const detectCurrency = () => {
  try {
    const saved = localStorage.getItem("jawa_currency");
    if (saved && CURRENCY_LIST.some((c) => c.code === saved)) return saved;
    const loc = (navigator.language || "").toUpperCase();
    const region = loc.split("-")[1] || "";
    if (region === "NG") return "NGN";
    if (region === "US") return "USD";
    if (region === "GB") return "GBP";
    if (EU_REGIONS.includes(region)) return "EUR";
  } catch {
    /* localStorage unavailable */
  }
  return "USD";
};

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(detectCurrency);
  const [rates, setRates] = useState(null);

  useEffect(() => {
    api.get("/rates").then((r) => setRates(r.data.rates)).catch(() => setRates(null));
  }, []);

  const setCurrency = (code) => {
    setCurrencyState(code);
    try {
      localStorage.setItem("jawa_currency", code);
    } catch {
      /* ignore */
    }
  };

  const value = useMemo(() => ({ currency, setCurrency, rates }), [currency, rates]);
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => useContext(CurrencyContext);

const rateKey = (c) => `ngn_per_${String(c).toLowerCase()}`;

// Convert an amount from its base currency to the target via the NGN pivot table.
// Returns null when rates are unavailable — callers must fall back to the base price (never invent a rate).
export const convertAmount = (amount, from, to, rates) => {
  if (amount == null || Number.isNaN(amount)) return null;
  if (from === to) return amount;
  if (!rates) return null;
  const perFrom = from === "NGN" ? 1 : rates[rateKey(from)];
  const perTo = to === "NGN" ? 1 : rates[rateKey(to)];
  if (!perFrom || !perTo) return null;
  return (amount * perFrom) / perTo;
};

export const formatMoney = (amount, code) => {
  if (amount == null || Number.isNaN(amount)) return "—";
  const sym = CURRENCY_LIST.find((c) => c.code === code)?.symbol || `${code} `;
  if (code === "NGN") return `${sym}${(Math.round(amount / 100) * 100).toLocaleString()}`;
  const rounded = amount >= 200 ? Math.round(amount) : Math.round(amount * 100) / 100;
  const hasFraction = Math.abs(rounded % 1) > 0.004;
  return `${sym}${rounded.toLocaleString(undefined, { minimumFractionDigits: hasFraction ? 2 : 0, maximumFractionDigits: 2 })}`;
};

// Display a price in the visitor's selected currency. The base price stays authoritative;
// when rates are unavailable the base price is shown with its own currency label.
export const Price = ({ amount, from = "GBP", className = "" }) => {
  const ctx = useCurrency();
  if (amount == null) return null;
  const currency = ctx?.currency || from;
  const converted = convertAmount(amount, from, currency, ctx?.rates);
  const text = converted == null ? formatMoney(amount, from) : formatMoney(converted, currency);
  return <span className={className}>{text}</span>;
};

// The amount that will actually be charged at Paystack checkout (always NGN), for disclosure.
export const ChargeNote = ({ amount, from = "GBP", className = "" }) => {
  const ctx = useCurrency();
  const ngn = convertAmount(amount, from, "NGN", ctx?.rates);
  if (ngn == null) return <span className={className}>Charged in naira (₦) at checkout via Paystack.</span>;
  return <span className={className}>Charged in naira at checkout via Paystack (≈ {formatMoney(ngn, "NGN")}).</span>;
};
